import { jsPDF } from "jspdf";
import { formatEur, MATERIAL_BASELINE } from "../data/shop";

const INK = [26, 26, 26];
const PAPER = [246, 245, 242];
const MUTED = [92, 90, 86];
const BORDER = [220, 217, 209];

const hexToRgb = (hex) => {
  const v = hex.replace("#", "");
  const n = parseInt(v.length === 3 ? v.split("").map((c) => c + c).join("") : v, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
};

const slugify = (s) =>
  (s || "")
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

export function buildSetDatasheetPdf(set) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const W = 595;
  const H = 842;
  const M = 56;

  const header = (label) => {
    doc.setFillColor(...INK);
    doc.rect(0, 0, W, 48, "F");
    doc.setTextColor(...PAPER);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.text("LIVINORA · DATENBLATT", M, 30);
    doc.text(label || "", W - M, 30, { align: "right" });
  };

  const footer = (n, total) => {
    doc.setDrawColor(...BORDER);
    doc.line(M, H - 56, W - M, H - 56);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(...MUTED);
    doc.text("Richtpreis. Lieferung und Montage auf Anfrage.", M, H - 36);
    doc.text(`${n} / ${total}`, W - M, H - 36, { align: "right" });
  };

  // ---------- Cover ----------
  header("Cover");
  doc.setTextColor(...INK);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text("STANDARD-SET · " + (set.style_label || "").toUpperCase(), M, 110);

  doc.setFont("times", "normal");
  doc.setFontSize(36);
  const titleLines = doc.splitTextToSize(set.name, W - M * 2);
  doc.text(titleLines, M, 152);

  doc.setFont("times", "italic");
  doc.setFontSize(16);
  doc.setTextColor(...MUTED);
  const sub = doc.splitTextToSize(set.subtitle || "", W - M * 2);
  doc.text(sub, M, 152 + titleLines.length * 38 + 6);

  // Palette swatches
  let py = 152 + titleLines.length * 38 + sub.length * 18 + 36;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...INK);
  doc.text("PALETTE", M, py);
  py += 10;
  (set.palette || []).forEach((hex, i) => {
    const { r, g, b } = hexToRgb(hex);
    doc.setFillColor(r, g, b);
    doc.setDrawColor(...BORDER);
    doc.rect(M + i * 42, py, 36, 36, "FD");
  });

  // Price
  doc.setFontSize(9);
  doc.setTextColor(...INK);
  doc.text("RICHTPREIS", M, py + 90);
  doc.setFont("times", "normal");
  doc.setFontSize(28);
  doc.text(`ab ${formatEur(set.priceFrom)}`, M, py + 122);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(...MUTED);
  doc.text("für das Standard-Set, zzgl. Lieferung und Montage.", M, py + 138);
  doc.text("Staffelpreise und Objektkonditionen auf Anfrage.", M, py + 152);

  footer(1, 2 + (set.components?.length || 0));

  // ---------- Set Overview ----------
  doc.addPage();
  header("Übersicht");
  let y = 110;
  doc.setTextColor(...INK);
  doc.setFont("times", "normal");
  doc.setFontSize(24);
  doc.text("Set-Übersicht", M, y);
  y += 28;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(...MUTED);
  const desc = doc.splitTextToSize(set.description || "", W - M * 2);
  doc.text(desc, M, y);
  y += desc.length * 13 + 16;

  // Meta table
  const meta = [
    ["Stilserie", set.style_label],
    ["Raum", set.room?.charAt(0).toUpperCase() + set.room?.slice(1)],
    ["Geeignet für", (set.suitedFor || []).join(", ")],
    ["Komponenten", String(set.components?.length || 0)],
    ["Richtpreis", `ab ${formatEur(set.priceFrom)} (${formatEur(set.priceFrom)} – ${formatEur(set.priceTo || set.priceFrom)})`],
  ];
  doc.setFontSize(9);
  meta.forEach(([k, v]) => {
    doc.setTextColor(...MUTED);
    doc.setFont("helvetica", "normal");
    doc.text(String(k).toUpperCase(), M, y);
    doc.setTextColor(...INK);
    doc.setFont("times", "normal");
    doc.setFontSize(13);
    const lines = doc.splitTextToSize(String(v || "—"), W - M * 2 - 160);
    doc.text(lines, M + 160, y);
    y += Math.max(20, lines.length * 15) + 6;
    doc.setFontSize(9);
    doc.setDrawColor(...BORDER);
    doc.line(M, y, W - M, y);
    y += 12;
  });

  // Component overview list
  y += 14;
  doc.setFont("times", "normal");
  doc.setFontSize(16);
  doc.setTextColor(...INK);
  doc.text("Komponenten im Set", M, y);
  y += 20;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(...INK);
  (set.components || []).forEach((co, i) => {
    if (y > H - 100) {
      footer(2, 2 + (set.components?.length || 0));
      doc.addPage();
      header("Übersicht");
      y = 110;
    }
    const num = String(i + 1).padStart(2, "0");
    doc.setTextColor(...MUTED);
    doc.text(num, M, y);
    doc.setTextColor(...INK);
    doc.text(co.title, M + 30, y);
    y += 18;
  });

  footer(2, 2 + (set.components?.length || 0));

  // ---------- One page per component ----------
  (set.components || []).forEach((co, i) => {
    doc.addPage();
    header(`Komponente ${String(i + 1).padStart(2, "0")} / ${String(set.components.length).padStart(2, "0")}`);

    doc.setTextColor(...INK);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text(`${set.name.toUpperCase()}`, M, 110);

    doc.setFont("times", "normal");
    doc.setFontSize(28);
    const tl = doc.splitTextToSize(co.title, W - M * 2);
    doc.text(tl, M, 142);

    let cy = 142 + tl.length * 30 + 20;
    doc.setDrawColor(...BORDER);
    doc.line(M, cy, W - M, cy);
    cy += 22;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(...MUTED);
    const dl = doc.splitTextToSize(co.desc || "", W - M * 2);
    doc.text(dl, M, cy);
    cy += dl.length * 14 + 18;

    const specs = [
      ["Material", co.material],
      ["Ausführung", co.finish],
      ["Maße", co.dimensions],
      ["Hinweis", co.note],
    ];
    specs.forEach(([k, v]) => {
      doc.setTextColor(...MUTED);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.text(String(k).toUpperCase(), M, cy);
      doc.setTextColor(...INK);
      doc.setFont("times", "normal");
      doc.setFontSize(12);
      const vl = doc.splitTextToSize(String(v || "—"), W - M * 2 - 160);
      doc.text(vl, M + 160, cy);
      cy += Math.max(20, vl.length * 14) + 6;
      doc.setDrawColor(...BORDER);
      doc.line(M, cy, W - M, cy);
      cy += 12;
    });

    footer(3 + i, 3 + set.components.length);
  });

  // ---------- Material baseline page ----------
  doc.addPage();
  header("Materialspezifikation");
  let my = 110;
  doc.setTextColor(...INK);
  doc.setFont("times", "normal");
  doc.setFontSize(24);
  doc.text("Konstruktion & Herkunft", M, my);
  my += 28;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(...MUTED);
  const intro = doc.splitTextToSize(
    "Korpus, Türen und Schubladen aus FSC®-zertifiziertem Holzwerkstoff. Beschläge und Auszüge in Metall, Füße aus Massivholz. Eine konsistente Konstruktionslogik über alle Stilserien hinweg.",
    W - M * 2
  );
  doc.text(intro, M, my);
  my += intro.length * 13 + 14;

  doc.setDrawColor(...BORDER);
  doc.line(M, my, W - M, my);
  my += 16;

  doc.setFontSize(9);
  MATERIAL_BASELINE.forEach((row) => {
    doc.setTextColor(...MUTED);
    doc.setFont("helvetica", "normal");
    doc.text(String(row.label).toUpperCase(), M, my);
    doc.setTextColor(...INK);
    doc.setFont("times", "normal");
    doc.setFontSize(12);
    const lines = doc.splitTextToSize(String(row.value), W - M * 2 - 200);
    doc.text(lines, M + 200, my);
    my += Math.max(20, lines.length * 14) + 6;
    doc.setFontSize(9);
    doc.setDrawColor(...BORDER);
    doc.line(M, my, W - M, my);
    my += 12;
  });

  footer(3 + (set.components?.length || 0), 3 + (set.components?.length || 0));

  const filename = `Livinora-Datenblatt-${slugify(set.slug || set.name)}.pdf`;
  doc.save(filename);
}
